/**
 * Create System Role Modal - Super Admin only
 * Creates a system role template from scratch: name, description, required isPaid
 * flag, and an OFF/READ/FULL permission matrix.
 *
 * Used by RoleManagementPage's "Add System Role" action.
 */

import { useState } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import {
  PermissionMatrix,
  PermissionValue,
  PermissionDefinition,
} from '../../components/roles/PermissionMatrix';
import { CreateRoleRequest } from '../../services/api/roleService';

interface CreateSystemRoleModalProps {
  allPermissions: PermissionDefinition[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (body: CreateRoleRequest) => void;
}

export const CreateSystemRoleModal = ({
  allPermissions,
  isSubmitting,
  onClose,
  onSubmit,
}: CreateSystemRoleModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  /** null = no explicit choice yet (required field — user must pick before submitting) */
  const [isPaid, setIsPaid] = useState<boolean | null>(null);
  const [permissions, setPermissions] = useState<PermissionValue[]>([]);
  const [showIsPaidError, setShowIsPaidError] = useState(false);

  const handleSubmit = () => {
    if (isPaid === null) {
      setShowIsPaidError(true);
      return;
    }
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      isPaid,
      permissions: permissions.map((p) => ({
        permissionId: p.permissionId,
        accessLevel: p.accessLevel,
      })),
    });
  };

  const isValid = name.trim().length > 0 && isPaid !== null;

  return (
    <Modal isOpen onClose={onClose} title="Create System Role">
      <ModalBody>
        <FieldGroup>
          <FieldLabel>Role name *</FieldLabel>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Org Admin, Clinician"
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Description</FieldLabel>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Briefly describe this role's purpose"
            rows={2}
          />
        </FieldGroup>

        {/* isPaid toggle — required, no default */}
        <FieldGroup>
          <FieldLabel>
            Paid role *
            <FieldHint>
              Marks this template as a billable seat. Derived org roles inherit this setting.
            </FieldHint>
          </FieldLabel>
          <IsPaidToggleGroup>
            <IsPaidOption
              $active={isPaid === false}
              onClick={() => {
                setIsPaid(false);
                setShowIsPaidError(false);
              }}
              type="button"
            >
              No — free seat
            </IsPaidOption>
            <IsPaidOption
              $active={isPaid === true}
              onClick={() => {
                setIsPaid(true);
                setShowIsPaidError(false);
              }}
              type="button"
            >
              Yes — paid seat
            </IsPaidOption>
          </IsPaidToggleGroup>
          {showIsPaidError && (
            <FieldError>Please choose whether this is a paid role before continuing.</FieldError>
          )}
        </FieldGroup>

        {/* Permissions (optional at creation) */}
        <FieldGroup>
          <FieldLabel>Permissions</FieldLabel>
          <PermissionMatrix
            permissions={allPermissions}
            value={permissions}
            onChange={setPermissions}
          />
        </FieldGroup>
      </ModalBody>

      <ModalFooter>
        <Button variant="secondary" size="sm" leftIcon={<X size={14} />} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary-blue"
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? 'Creating...' : 'Create Role'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateSystemRoleModal;

// ==================== Styled Components ====================

export const IsPaidToggleGroup = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  flex-shrink: 0;
`;

export const IsPaidOption = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary.base : theme.colors.backgrounds.white};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.white : theme.colors.text.secondary};

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.borders.default};
  }

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primary.base : theme.colors.backgrounds.gray};
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  padding: ${({ theme }) => theme.spacing[5]};
  max-height: 70vh;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldHint = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FieldError = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: ${({ theme }) => theme.spacing[1]} 0 0;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  resize: vertical;
  font-family: inherit;
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary.base}20;
  }
`;
